import type { WidgetPage } from './core';

export interface PaymentInitiationRequest {
  sourceAccountId: string;
  beneficiaryId: string;
  amount: number;
  currency?: string;
  reference?: string;
  transferReasonId?: string;
  /**
   * Future-dates the payment: a 'YYYY-MM-DD' calendar date, today or later.
   * Maps to the wire `schedulePaymentDate`, which the platform honours by
   * queuing the transfer instead of executing it (on integratedFinance
   * projects the date instead rides through to the provider, which schedules
   * it provider-side). The confirmation is `result.status === 'scheduled'`
   * with `result.scheduledAt` echoing this date.
   *
   * The backend applies no validation to the field at all (`@IsString` and
   * nothing else — a past date would become a job that fires on the next
   * poll), so the hosts enforce the native flow's own client-side rule,
   * `isValidScheduleDate`: malformed or past dates reject before any HTTP
   * call (see `invalidScheduledAtReason`).
   *
   * On a project that also runs a payment-cancellation window the window
   * wins: the result is 'cancellable' (carrying BOTH scheduling fields) and
   * the future date is honoured after the window closes.
   */
  scheduledAt?: string;
}

/**
 * What a *successful* `payments.initiate` actually did. Accepting a payment is
 * not the same as having sent it, and the platform has three genuinely different
 * outcomes behind one 200 — they are told apart only by which scheduling field
 * the wire body carries, which is why this discriminant exists.
 *
 * - 'completed'   — the transfer was executed there and then. `transactionId` is
 *                   a real payment id; there is nothing left to cancel.
 * - 'scheduled'   — future-dated. Nothing has moved and nothing will until
 *                   `scheduledAt`; `transactionId` is the *job* id, not a payment
 *                   id, so a transaction listing will not show it yet.
 * - 'cancellable' — the project runs a payment-cancellation window: the transfer
 *                   is queued and will be sent when the window closes at
 *                   `expiryReversalAt`, unless the customer cancels first via
 *                   `payments.scheduled.cancel` (the id to pass is
 *                   `transactionId`, which is the reversal job id).
 *
 * A widget that says "payment sent" on all three is wrong twice.
 */
export type PaymentInitiationStatus = 'completed' | 'scheduled' | 'cancellable';

export interface PaymentInitiationResult {
  /**
   * Identifies whatever was created. A payment id on 'completed'; a pg-boss job
   * id on 'scheduled' and 'cancellable' (see PaymentInitiationStatus).
   */
  transactionId?: string;
  /**
   * Which of the three outcomes this was — read it rather than sniffing
   * `scheduledAt`/`expiryReversalAt` yourself.
   *
   * Optional because a host that predates the field never sets it: **treat
   * absence as 'completed'**, which is what widgets assumed before it existed.
   * Every App SDK host sets it, via `derivePaymentInitiationStatus`.
   */
  status?: PaymentInitiationStatus;
  /**
   * When a 'scheduled' payment is due to execute (ISO 8601). Present only on
   * 'scheduled'.
   */
  scheduledAt?: string;
  /**
   * When a 'cancellable' payment's cancellation window closes and the transfer
   * is sent (ISO 8601). Present only on 'cancellable' — drive a countdown off
   * it.
   */
  expiryReversalAt?: string;
  [key: string]: unknown;
}

/**
 * The single derivation of PaymentInitiationStatus from a raw
 * `POST /payments` body, so no host has to invent its own and drift from the
 * others.
 *
 * The two fields are the platform's own discriminators, not a convention
 * invented here: `SchedulePaymentsServices.determinePaymentExecutionStrategy`
 * either returns a cancellation-window job — stamped `expiryReversalAt` by
 * `paymentCancellationScheduleTransferPayment` — or a future-dated job stamped
 * `scheduledAt` by `scheduleTransferPayment`, or defers to the provider, whose
 * response carries neither. They are the only two scheduling fields on
 * `TransferMoneyResponse`, and every other field is optional on all three, so
 * nothing else can tell the outcomes apart.
 *
 * `expiryReversalAt` is checked FIRST and that order is load-bearing: the
 * cancellation-window path spreads the original request's schedule date through
 * as `scheduledAt`, so a future-dated payment on a project that also runs a
 * cancellation window carries BOTH. It is a cancellable payment — the window is
 * the thing the customer can act on now.
 */
export function derivePaymentInitiationStatus(
  raw: Record<string, unknown> | null | undefined,
): PaymentInitiationStatus {
  if (hasWireValue(raw?.['expiryReversalAt'])) return 'cancellable';
  if (hasWireValue(raw?.['scheduledAt'])) return 'scheduled';
  return 'completed';
}

/** A wire field is "present" only if it carries something; null/'' mean absent. */
function hasWireValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * The one scheduling-date rule every host (and the mock) enforces for
 * `PaymentInitiationRequest.scheduledAt`, mirrored from the native flow's
 * `isValidScheduleDate` (payment-utils.ts): a 'YYYY-MM-DD' calendar date,
 * today or later, where "today" is the host's local calendar day — the same
 * comparison dayjs's `isSameOrAfter(dayjs(), 'day')` makes natively.
 *
 * The rule lives client-side on purpose, and this helper exists so no host
 * re-invents it: `POST /payments` validates `schedulePaymentDate` as nothing
 * more than an optional string and parses whatever arrives, so a host that
 * forwarded a past or malformed date would create a job that fires on the
 * queue's next poll — a "scheduled" payment that executes immediately.
 *
 * Returns the problem as a sentence, or null when the date is acceptable.
 */
export function invalidScheduledAtReason(value: string, today: Date = new Date()): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `scheduledAt must be a 'YYYY-MM-DD' calendar date, got '${value}'`;
  }
  // Round-trip through Date components: JS rolls an out-of-range day over into
  // the next month ('2026-02-31' becomes March 2nd/3rd), so equality after the
  // trip — not parseability — is what proves the date exists.
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return `scheduledAt is not a real calendar date: '${value}'`;
  }
  const pad = (part: number) => String(part).padStart(2, '0');
  const localToday = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  if (value < localToday) {
    return `scheduledAt must be today or later (today is ${localToday}), got '${value}'`;
  }
  return null;
}

/** Confirmation of Payee (UK Faster Payments name check). */
export type CopMatchStatus = 'match' | 'close_match' | 'no_match' | 'not_supported';

export interface CopCheckRequest {
  name: string;
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
  accountType: 'personal' | 'business';
}

export interface CopCheckResult {
  status: CopMatchStatus;
  /** The registered account name, returned on close matches where permitted. */
  actualName?: string;
  reasonCode?: string;
}

/** A transfer/payment reason as configured for the project's provider. */
export interface WidgetTransferReason {
  id: string;
  description: string;
}

export interface WidgetTransactionParty {
  name: string;
  accountNumber?: string;
  sortCode?: string;
  iban?: string;
}

export interface WidgetTransactionDetail {
  transactionId: string;
  amount: number;
  currency?: string;
  date?: string;
  postingDate?: string;
  reference?: string;
  /** Direction: 'incoming' | 'outgoing'. */
  type?: string;
  status?: string;
  feeAmount?: number;
  feeCurrency?: string;
  totalAmount?: number;
  sender?: WidgetTransactionParty;
  receiver?: WidgetTransactionParty;
}

/** A pending/scheduled payment still open for cancellation (reversal). */
export interface WidgetScheduledPayment {
  paymentId: string;
  amount: number;
  currency?: string;
  beneficiaryName?: string;
  reference?: string;
  sourceAccountId?: string;
  /**
   * When the cancellation window closes and the transfer is sent (ISO 8601) —
   * the job's own fire time, and the same instant
   * `PaymentInitiationResult.expiryReversalAt` reported when the payment was
   * created. Every row of `payments.scheduled.list` is a cancellation-window
   * job, so this is "when it fires" for the whole listing; drive a countdown
   * off it. Optional only because a host predating this field forwards
   * nothing.
   */
  expiryReversalAt?: string;
  /**
   * The future execution date the payment was created with ('YYYY-MM-DD' —
   * the initiation payload's `scheduledAt`). Present only when the payment
   * was future-dated as well as window-gated: the transfer executes on this
   * date once the cancellation window closes, not at `expiryReversalAt`.
   */
  scheduledAt?: string;
}

export interface WidgetPaymentRequest {
  paymentRequestId: string;
  amount: number;
  currency: string;
  reference?: string;
  reason?: string;
  /** Approval state, e.g. 'pending' | 'approved' | 'rejected'. */
  status?: string;
  createdDate?: string;
  approvedOrRejectedDate?: string;
  requesterName?: string;
  approverName?: string;
  beneficiaryName?: string;
  accountId?: string;
  accountName?: string;
}

/**
 * Asking an approver to fund a payment (the requester side of the
 * payment-request flow — not an invoice/request-to-pay to a third party).
 * sourceAccountId defaults to the currently selected account.
 *
 * `reference` and `transferReasonId` are optional in shape but required in
 * practice: the native route (POST /payments/request) rejects a request whose
 * paymentReference or transfer reason is empty, so hosts fail the call with an
 * invalid-payload error before any HTTP happens when either is missing. Pick
 * `transferReasonId` from `payments.reasons.list` — the host resolves the
 * reason's human-readable label from that same catalogue on your behalf (an id
 * the catalogue does not contain is rejected), so there is deliberately no
 * label field here.
 */
export interface PaymentRequestCreateRequest {
  beneficiaryId: string;
  amount: number;
  currency: string;
  sourceAccountId?: string;
  reference?: string;
  transferReasonId?: string;
}

export interface PaymentRequestCreateResult {
  paymentRequestId: string;
}

/**
 * An international payment. Same fields as PaymentInitiationRequest minus
 * `currency` (the destination currency comes from the beneficiary, not the
 * caller): the platform sends both domestic and international payments to the
 * same endpoint and the provider picks the rail from the beneficiary's
 * country/currency. There is deliberately no scheme/rail override here —
 * the native international flow cannot send one either.
 */
export interface InternationalPaymentRequest {
  sourceAccountId: string;
  beneficiaryId: string;
  amount: number;
  reference?: string;
  transferReasonId?: string;
}

/**
 * The fee/total breakdown of an international payment, as answered by a
 * precheck (dry run) — nothing has moved. `totalAmount` is what would leave the
 * account including fees, which is what the native flow compares against the
 * balance before letting the customer continue.
 */
export interface InternationalPaymentPrecheckResult {
  amount: number;
  currency: string;
  feeAmount?: number;
  totalAmount?: number;
  receiverName?: string;
  receiverAccountNumber?: string;
  senderName?: string;
  senderAccountNumber?: string;
}

/**
 * The executed international payment. `transactionId` is absent when the
 * provider answers a bare fee breakdown with no identifier — treat it as
 * optional and re-read the transaction listing rather than depending on it.
 */
export interface InternationalPaymentResult extends InternationalPaymentPrecheckResult {
  transactionId?: string;
}

/** One uploaded bulk-payment batch (master), as listed by bulkPayments.list. */
export interface WidgetBulkPayment {
  bulkPaymentId: string;
  /** Batch status, e.g. 'pending' | 'in progress' | 'completed' | 'cancelled'. */
  status: string;
  fileName?: string;
  accountId?: string;
  accountName?: string;
  currency?: string;
  totalAmount: number;
  totalPayments?: number;
  totalSuccessPayments?: number;
  totalFailedPayments?: number;
  totalDuplicatePayments?: number;
  totalSchedulePayments?: number;
  totalUniqueBeneficiaries?: number;
  totalErrors?: number;
  createdDate?: string;
  executedDate?: string;
}

/** One row (a single transfer) inside a bulk-payment batch. */
export interface WidgetBulkPaymentTransfer {
  transferId: string;
  beneficiaryName: string;
  beneficiaryId?: string;
  amount: number;
  currency: string;
  /** Row status within the batch. */
  status: string;
  /** Status of the payment the row produced, once one exists. */
  transactionStatus?: string;
  transactionId?: string;
  reference?: string;
  scheduleType?: string;
  scheduleDate?: string;
  /** Source CSV row number, when the batch kept it. */
  row?: string;
  isDuplicate?: boolean;
  isError?: boolean;
  /** Native CoP/VoP result codes; absent means no result, not a successful match. */
  beneficiaryStatus?: string;
  beneficiaryCoPCode?: string;
  beneficiaryCoPStatus?: string;
  beneficiaryCoPName?: string;
  beneficiaryExistStatus?: string;
  beneficiaryExistAndCopStatus?: string;
  errors?: string[];
}

/** Batch-level totals served alongside a bulk-payment's rows. */
export interface WidgetBulkPaymentSummary {
  /** Native approval record; use paymentRequests.get for its current state. */
  paymentRequestId?: string;
  totalPendingBeneficiaries?: number;
  totalPendingTransactions?: number;
  /** Master status of the batch. */
  status: string;
  totalAmount?: number;
  currency?: string;
  accountId?: string;
  accountName?: string;
  totalPayments?: number;
  totalSuccessPayments?: number;
  totalFailedPayments?: number;
  totalDuplicatePayments?: number;
  totalSchedulePayments?: number;
  totalPendingPayments?: number;
  totalUniqueBeneficiaries?: number;
  totalErrors?: number;
  executedDate?: string;
}

/** A page of a batch's transfers plus the batch totals. */
export interface WidgetBulkPaymentDetail extends WidgetPage<WidgetBulkPaymentTransfer> {
  bulkPaymentId: string;
  /** Absent when the provider returned no summary metadata. */
  summary?: WidgetBulkPaymentSummary;
}

/** A payment rail the project can send on, as offered for a country/currency. */
export interface WidgetPaymentScheme {
  /** Display label, e.g. 'Faster Payments'. */
  label: string;
  /** Rail identifier, e.g. 'faster-payments' | 'swift' | 'sepa' | 'ach'. */
  value: string;
}

/** Native bulk-payment CSV template. Download as text/csv; populate rows before upload. */
export const BULK_PAYMENT_CSV_TEMPLATE =
  'Country,Currency,Full Name / Org. Name,Account number,Sort Code,IBAN,BIC (Optional),Type(Personal/Business),Address line 1 (Optional),Address line 2 (Optional),State  (Optional),City  (Optional),Post Code  (Optional),Amount,Schedule Payment(DD/MM/YYYY),Payment Reference,Payment Reason (Payment for goods and services / Direct capital investment / Salary/Payroll / Refund / Loan / Repayment of loan / Merchant Settlement / Intercompany/group transfer / Tax Settlement / Expenses / Other / Property purchase / Mortage repayment)\n';
